import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, ApiError } from '../api/client'

export interface AdminSession {
  email: string
  role: 'admin' | 'viewer'
  integratorId: string
  integratorSlug: string
  integratorName: string
  isSystemAdmin: boolean
  expiresAt: string
}

interface AuthContextValue {
  session: AdminSession | null
  loading: boolean
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AdminSession | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const me = await api.auth.me()
      setSession(me)
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSession(null)
      } else {
        console.error('Auth me() failed:', err)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const signOut = useCallback(async () => {
    try {
      await api.auth.logout()
    } finally {
      setSession(null)
    }
  }, [])

  return (
    <AuthContext.Provider value={{ session, loading, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
